import type { ErrorCode, FeatureKey, Platform, Role } from '@iptv/shared-types';

/**
 * English catalog for the merchant and admin web panels. This is the reference catalog: every
 * other language is type-checked against its shape, so a missing key fails the build.
 */
export const en = {
  common: {
    appTitleMerchant: 'Merchant Panel',
    appTitleAdmin: 'Platform Admin',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    add: 'Add',
    confirm: 'Confirm',
    remove: 'Remove',
    upload: 'Upload',
    replace: 'Replace',
    search: 'Search',
    refresh: 'Refresh',
    loading: 'Loading…',
    yes: 'Yes',
    no: 'No',
    enabled: 'Enabled',
    disabled: 'Disabled',
    on: 'On',
    off: 'Off',
    never: 'Never',
    none: 'None',
    all: 'All',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    email: 'Email',
    createdAt: 'Created',
    updatedAt: 'Updated',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} of {{pages}}',
    totalItems: '{{count}} items',
    empty: 'Nothing here yet.',
    saved: 'Changes saved.',
    deleted: 'Deleted.',
    copied: 'Copied to clipboard.',
    copy: 'Copy',
    language: 'Language',
    arabic: 'العربية',
    english: 'English',
    theme: 'Theme',
    signOut: 'Sign out',
    unsavedChanges: 'You have unsaved changes.',
    optional: 'Optional',
    required: 'Required',
    retry: 'Retry',
    areYouSure: 'Are you sure?',
    cannotUndo: 'This action cannot be undone.',
    ms: '{{value}} ms',
    arabicText: 'Arabic text',
    englishText: 'English text',
    more: 'More',
    menu: 'Menu',
    mainNavigation: 'Main navigation',
    skipToContent: 'Skip to content',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    clearSearch: 'Clear search',
  },
  nav: {
    dashboard: 'Dashboard',
    hosts: 'IPTV Hosts',
    branding: 'Branding',
    featured: 'Featured sections',
    settings: 'App settings',
    devices: 'Devices',
    users: 'Team',
    audit: 'Activity log',
    account: 'My account',
    tenants: 'Merchants',
    plans: 'Plans',
    releases: 'App releases',
    globalSettings: 'Global settings',
    announcements: 'Announcements',
    hostLibrary: 'Host library',
  },
  navShort: {
    dashboard: 'Home',
    hosts: 'Hosts',
    branding: 'Branding',
    featured: 'Featured',
    settings: 'Settings',
    devices: 'Devices',
    users: 'Team',
    audit: 'Activity',
    tenants: 'Merchants',
    plans: 'Plans',
    releases: 'Releases',
    globalSettings: 'Settings',
    announcements: 'News',
    hostLibrary: 'Hosts',
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitleMerchant: 'Manage your store, hosts and customers.',
    signInSubtitleAdmin: 'Platform administration.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    sessionExpired: 'Your session has ended. Please sign in again.',
    wrongPanel: 'This account cannot use this panel.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordsDontMatch: 'Passwords do not match.',
    passwordRules: 'At least 12 characters with upper case, lower case and a digit.',
    passwordChanged: 'Password changed. Other sessions were signed out.',
    profile: 'Profile',
    role: 'Role',
  },
  twoFactor: {
    title: 'Two-step verification',
    description:
      'After your password, sign-in also asks for a code from an authenticator app on your phone.',
    on: 'On',
    off: 'Off',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    turnedOff: 'Two-step verification is off.',
    turnOffIntro: 'Enter your password and a current code to turn it off.',
    requiredNote: 'Required for your account, so it cannot be turned off.',
    setupTitle: 'Set up two-step verification',
    setupRequired: 'This account must use two-step verification. It takes a minute.',
    setupIntro:
      'Add this account to an authenticator app such as Google Authenticator, Microsoft Authenticator or your iPhone passwords, then enter the 6-digit code it shows.',
    openApp: 'Add to authenticator app',
    qrLabel: 'QR code for your authenticator app',
    manualKey: 'Or enter this key in the app',
    code: 'Code',
    codeHint: 'The 6-digit code from the app, or one of your recovery codes.',
    enterCode: 'Enter the 6-digit code from your authenticator app.',
    verify: 'Verify',
    back: 'Back',
    expired: 'Sign-in timed out. Enter your password again.',
    recoveryCodes: 'Recovery codes',
    recoveryIntro: 'Keep these somewhere safe. Each one signs you in once if you lose your phone.',
    recoveryWarning:
      'This is the only time these codes are shown. Save them now: without your phone or a code you cannot sign in.',
    copyAll: 'Copy all',
    download: 'Download',
    savedCodes: 'I saved my codes',
    newCodes: 'New recovery codes',
    newCodesIntro: 'Your old recovery codes stop working. Enter a current code to continue.',
    reset: 'Reset two-step verification',
    resetConfirm:
      'Reset two-step verification for {{name}}? They are signed out everywhere and set it up again at their next sign-in if it is required.',
    resetDone: 'Two-step verification was reset.',
  },
  roles: {
    SUPER_ADMIN: 'Super admin',
    MERCHANT_OWNER: 'Owner',
    MERCHANT_ADMIN: 'Admin',
    MERCHANT_SUPPORT: 'Support',
    VIEWER: 'Viewer',
  } satisfies Record<Role, string>,
  statuses: {
    ACTIVE: 'Active',
    SUSPENDED: 'Suspended',
    DISABLED: 'Disabled',
    ARCHIVED: 'Archived',
    BLOCKED: 'Blocked',
    UNKNOWN: 'Unknown',
    HEALTHY: 'Healthy',
    DEGRADED: 'Degraded',
    DOWN: 'Down',
  },
  platforms: {
    android_mobile: 'Android phone',
    android_tv: 'Android TV',
    fire_tv: 'Fire TV',
    ios: 'iPhone',
    ipados: 'iPad',
    tvos: 'Apple TV',
    samsung_tv: 'Samsung TV',
    lg_tv: 'LG TV',
  } satisfies Record<Platform, string>,
  channels: {
    play: 'Google Play',
    appstore: 'App Store',
    amazon: 'Amazon Appstore',
    direct_apk: 'Direct APK',
  },
  features: {
    enableLive: 'Live TV',
    enableMovies: 'Movies',
    enableSeries: 'Series',
    enableEPG: 'TV guide (EPG)',
    enableFavorites: 'Favorites',
    enableContinueWatching: 'Continue watching',
    enableSearch: 'Search',
    enableSupport: 'Support screen',
    enableExternalPlayer: 'External player',
    enableParentalControls: 'Parental controls',
    enableMultiProfile: 'Multiple profiles',
  } satisfies Record<FeatureKey, string>,
  dashboard: {
    title: 'Dashboard',
    storeCode: 'Store code',
    storeCodeHint: 'Customers enter this code once in the app to load your store.',
    plan: 'Plan',
    expires: 'Subscription ends',
    noExpiry: 'No end date',
    hosts: 'Hosts',
    limitOf: '{{used}} of {{limit}}',
    noHostsWarning: 'Add at least one IPTV host so customers can sign in.',
    addHost: 'Add host',
  },
  hosts: {
    title: 'IPTV hosts',
    subtitle:
      'Hosts are stored encrypted and are never shown to customers. The app picks the primary host and fails over to backups automatically.',
    add: 'Add host',
    editTitle: 'Edit host',
    createTitle: 'New host',
    label: 'Label',
    labelHint: 'For your team only, for example "Main server".',
    url: 'Server URL',
    urlHint: 'The base address your provider gave you, for example https://panel.example.com:8443',
    urlKeep: 'Leave empty to keep the current address.',
    providerKind: 'Provider type',
    xtream: 'Xtream-compatible',
    role: 'Role',
    PRIMARY: 'Primary',
    BACKUP: 'Backup',
    priority: 'Priority',
    priorityHint: 'Lower numbers are tried first among hosts with the same role.',
    enabled: 'Enabled',
    health: 'Health',
    latency: 'Latency',
    subscribers: 'Subscribers',
    subscribersOnline: '{{count}} now',
    subscribersToday: '{{count}} today',
    subscribersDetail: '{{today}} today · {{signedIn}} signed in',
    subscribersHint:
      'Who is using this host now (last 5 minutes) and today, and how many subscribers are signed in on it. Subscribers move to another host automatically when one fails.',
    monitorTitle: 'Check logins',
    monitorIntro:
      'One subscriber login per provider tests each of its hosts every 5 minutes. A host failing for 15 minutes while another host of the same provider works is taken out, comes back by itself once it works, and is deleted after 3 days out. Logins are stored encrypted.',
    monitorChecked: 'Last check {{when}}',
    monitorRejected:
      'Every host refused this login, so it expired or is wrong. No host is taken out until you update it.',
    monitorUsername: 'Username',
    monitorPassword: 'Password',
    monitorSave: 'Save and check now',
    monitorChange: 'Change login',
    monitorRemove: 'Remove login',
    monitorRemoveConfirm:
      'Remove this check login? Hosts stay as they are and none is taken out or deleted automatically.',
    monitorRecent: 'Last 7 days',
    provider: 'Provider',
    providerHint:
      'Hosts of one provider are tested with its login and only compared with each other.',
    noProvider: 'No provider',
    allHosts: 'All hosts',
    hostCount: '{{count}} hosts',
    addProvider: 'Add provider',
    providerName: 'Provider name',
    renameProvider: 'Rename',
    deleteProvider: 'Delete provider',
    deleteProviderConfirm:
      'Delete the provider "{{name}}"? Its hosts stay and use the login of hosts without a provider.',
    loginNone: 'No login',
    loginRefused: 'Refused',
    setLogin: 'Save login',
    providerHosts: 'Hosts',
    providerHostsTitle: '{{name}} hosts',
    providerHostsIntro:
      'Tick the hosts of this provider. Ticked hosts move to it; unticked ones go back to no provider. Subscribers are not affected.',
    selectLoose: 'Tick every host without a provider',
    selectNone: 'Clear',
    currentlyIn: 'Now in: {{name}}',
    selectedCount: '{{count}} ticked',
    auto_disabled: 'Taken out',
    auto_enabled: 'Back in',
    auto_deleted: 'Deleted',
    autoDisabled: 'Taken out automatically',
    failingSince: 'Failing, started {{when}}',
    lastChecked: 'Last checked',
    test: 'Test connection',
    testing: 'Testing…',
    testOk: 'Host reachable in {{ms}} ms.',
    testFailed: 'Host check failed: {{reason}}.',
    testTitle: 'Test "{{label}}"',
    testIntro:
      'Leave it empty to use the saved check login of the host’s provider, or type a subscriber login for this test only (never saved).',
    testUsername: 'Subscriber username (optional)',
    testPassword: 'Subscriber password (optional)',
    runTest: 'Run test',
    testOkAccount: 'Host works ({{ms}} ms), but this account is {{reason}}.',
    deleteConfirm: 'Delete host "{{label}}"? Customers on this host will move to another host.',
    empty: 'No hosts yet. Add the server address your provider gave you.',
    history: 'Recent checks',
    limitReached: 'Your plan allows {{limit}} hosts.',
    honestNote:
      'Hiding the host makes it hard to find, not impossible: a determined user with network tools on their own device can still observe traffic. Protected mode adds a gateway in front of the stream.',
    addMany: 'Add several',
    addManyTitle: 'Add several hosts',
    addManyIntro:
      'Paste the links of one provider, one per line. They work as one server: if one is blocked or down, customers move to the next automatically.',
    links: 'Links',
    linksHint: 'Full M3U links work too: only the server address is kept.',
    linksCount: '{{count}} links',
    namePrefix: 'Name',
    namePrefixHint: 'Hosts are named "{{label}} 1", "{{label}} 2" and so on.',
    namePrefixDefault: 'Server',
    testAccountIntro:
      'Leave it empty to use the saved check login of the chosen provider, or type a subscriber login for this test only (never saved).',
    testLinks: 'Test links',
    testAgain: 'Test again',
    addWorking: 'Add working ({{count}})',
    addFastest: 'Add the {{count}} fastest',
    roomLeft: 'Your plan allows {{limit}} hosts, so you can add {{room}} more.',
    noRoom: 'Your plan allows {{limit}} hosts and all are in use. Delete a host to add another.',
    added: '{{count}} hosts added.',
    link: 'Link',
    result: 'Result',
    works: 'Works',
    worksIn: 'Works · {{ms}} ms',
    failed: 'Failed',
    existing: 'Already added',
    repeated: 'Repeated above',
    testAll: 'Test all',
    testAllTitle: 'Test all hosts',
    testAllIntro:
      'Leave it empty to test each host with its provider’s saved check login, or type a subscriber login for this test only (never saved).',
    deleteFailed: 'Delete failed ({{count}})',
    deleteFailedConfirm:
      'Delete {{count}} hosts that failed the test? Customers move to the working ones.',
    allWork: 'All hosts work.',
  },
  hostCheck: {
    open: 'Check from Saudi Arabia',
    title: "Check hosts from your subscribers' country",
    intro:
      'Our server tests from Europe, so a host blocked in Saudi Arabia still shows as working here. Give this link to someone there: they open it on their phone, every host is tested from their network, and the result appears here next to each host.',
    unavailable: 'Check links are not enabled on the platform yet.',
    create: 'Create link',
    created: 'Created {{when}}',
    howTo:
      'They open it exactly as sent (it starts with http://) on mobile data or home Wi-Fi without a VPN, then tap "Start". An iPhone is best: it can also try playing a channel. A subscriber login per provider is saved once on their phone.',
    secret:
      'Anyone with the link sees your host addresses and saved check logins. Share it only with people you trust, and renew it if it leaks.',
    renew: 'Renew link',
    renewConfirm: 'Renew the link? The old one stops working at once.',
    delete: 'Delete link',
    deleteConfirm: 'Delete the check link? It stops working at once; the last results stay.',
    badge: 'Saudi Arabia: {{verdict}}',
    WORKS: 'works',
    BLOCKED: 'blocked',
    DOWN: 'down',
    NO_PLAYBACK: "didn't play",
    UNCONFIRMED: 'unconfirmed',
    hint_WORKS: 'The phone reached the host itself',
    hint_BLOCKED:
      "Works from our server, but the phone's network doesn't reach it or answers in its place",
    hint_DOWN: 'Answers neither the phone nor our server',
    hint_NO_PLAYBACK: "Answers, but a test channel didn't play on the phone",
    hint_UNCONFIRMED:
      "Something answered, but the browser can't tell it was the host; check from an iPhone",
  },
  hostReasons: {
    INVALID_URL: 'not a valid link',
    NETWORK: 'cannot connect',
    TIMEOUT: 'no answer in time',
    BAD_RESPONSE: 'unexpected answer, check the address',
    UPSTREAM_ERROR: 'the server returned an error',
    INVALID_CREDENTIALS: 'credentials rejected',
    ACCOUNT_EXPIRED: 'account expired',
    ACCOUNT_DISABLED: 'account disabled',
    NOT_FOUND: 'not found, check the address',
    BLOCKED_DESTINATION: 'private or local addresses are not allowed',
    NEEDS_ACCOUNT:
      'the server answers but hides itself from checks without an account. Test with a subscriber login',
  },
  branding: {
    title: 'Branding',
    subtitle: 'How your store looks in the app. Changes reach customers on their next launch.',
    identity: 'Identity',
    storeName: 'Store name',
    appName: 'App name',
    colors: 'Colors',
    primary: 'Primary',
    secondary: 'Secondary',
    accent: 'Accent',
    background: 'Background',
    surface: 'Surface',
    onPrimary: 'Text on primary',
    text: 'Text',
    buttonStyle: 'Button style',
    ROUNDED: 'Rounded',
    PILL: 'Pill',
    SQUARE: 'Square',
    assets: 'Images',
    logo: 'Logo',
    splash: 'Splash screen',
    background_img: 'Home background',
    loginBackground: 'Login background',
    assetHint: 'PNG, JPEG or WebP, up to 5 MB.',
    assetTooLarge: 'The file is larger than 5 MB.',
    assetWrongType: 'Use a PNG, JPEG or WebP image.',
    support: 'Support contacts',
    phone: 'Phone',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    website: 'Website',
    termsUrl: 'Terms of service URL',
    privacyUrl: 'Privacy policy URL',
    messages: 'Messages',
    welcome: 'Welcome message',
    loginHint: 'Login hint',
    emptyState: 'Empty state message',
    maintenance: 'Maintenance message',
    preview: 'Preview',
    previewLogin: 'Login screen',
    previewUsername: 'Username',
    previewPassword: 'Password',
    previewSignIn: 'Sign in',
    fullBrandingLocked:
      'Your plan includes basic branding. Upgrade to customize the button style, messages and extra images.',
  },
  settings: {
    title: 'App settings',
    playback: 'Playback',
    mediaMode: 'Stream delivery',
    DIRECT: 'Direct',
    DIRECT_hint:
      'The app plays streams straight from your host. Fastest, the stream address is visible to the device.',
    GATEWAY: 'Protected gateway',
    GATEWAY_hint:
      'Streams go through short-lived signed links from our gateway. The host address is not sent to the device.',
    gatewayLocked: 'Protected gateway is not included in your plan.',
    autoPlayNext: 'Play next episode automatically',
    language: 'Default app language',
    maintenance: 'Maintenance mode',
    maintenanceHint: 'Customers see your maintenance message instead of the catalog.',
    maintenanceMessage: 'Maintenance message',
    expiryReminder: 'Subscription ending reminder',
    expiryReminderHint:
      "Shown on the app's Home to customers whose provider subscription is about to end.",
    expiryReminderEnabled: 'Remind customers before their subscription ends',
    expiryDaysBefore: 'Start reminding this many days before it ends',
    expiryDaysPreset: '{{count}} days before',
    expiryMessage: 'Reminder text',
    expiryMessageHint:
      "Write {days} where the days left go. Leave it empty to use the app's own wording.",
    expiryPreview: 'What a customer sees with {{days}} days left:',
    expiryDefaultMessage:
      'Your subscription ends in {days} days. Contact your store to renew so your service is not interrupted.',
    features: 'Features',
    featuresHint: 'Turn app sections on or off. Features not in your plan cannot be enabled.',
    notInPlan: 'Not in plan',
    parental: 'Parental controls',
    parentalEnabled: 'Allow customers to lock categories with a PIN',
    hideAdult: 'Hide categories flagged as adult by the provider',
    minVersions: 'Minimum app versions',
    minVersionsHint: 'Customers below this version are asked or required to update.',
    minimumVersion: 'Minimum version',
    updateType: 'Update type',
    OPTIONAL: 'Optional',
    REQUIRED: 'Required',
    noMinimum: 'No minimum',
  },
  devices: {
    title: 'Devices',
    subtitle: 'Devices your customers signed in from.',
    searchPlaceholder: 'Search by username or device name',
    customer: 'Customer',
    device: 'Device',
    platform: 'Platform',
    appVersion: 'App version',
    lastActivity: 'Last activity',
    sessions: 'Sessions',
    logout: 'Sign out device',
    block: 'Block',
    unblock: 'Unblock',
    logoutConfirm: 'Sign out all sessions on this device?',
    blockConfirm:
      'Block this device? It will be signed out and cannot sign in again until unblocked.',
    loggedOut: 'Device signed out.',
    blocked: 'Device blocked.',
    unblocked: 'Device unblocked.',
    anyStatus: 'Any status',
    anyPlatform: 'Any platform',
  },
  users: {
    title: 'Team',
    subtitle: 'People who can sign in to this panel.',
    add: 'Invite member',
    createTitle: 'New team member',
    editTitle: 'Edit team member',
    displayName: 'Name',
    initialPassword: 'Initial password',
    initialPasswordHint: 'Share it privately. They can change it from My account.',
    lastLogin: 'Last sign-in',
    deleteConfirm: 'Remove {{name}} from the team?',
    you: 'You',
    limitReached: 'Your plan allows {{limit}} team members.',
  },
  audit: {
    title: 'Activity log',
    subtitle: 'A record of changes made in this account.',
    when: 'When',
    actor: 'Actor',
    action: 'Action',
    target: 'Target',
    ip: 'IP',
    details: 'Details',
    filterAction: 'Filter by action',
    USER: 'Team member',
    CUSTOMER: 'Customer',
    SYSTEM: 'System',
  },
  tenants: {
    title: 'Merchants',
    add: 'New merchant',
    createTitle: 'New merchant',
    editTitle: 'Edit merchant',
    searchPlaceholder: 'Search by name or store code',
    name: 'Merchant name',
    plan: 'Plan',
    storeCode: 'Store code',
    storeCodeHint: 'Leave empty to generate one. 4 to 16 letters or digits.',
    expiresAt: 'Subscription ends',
    maxDevicesOverride: 'Device limit override',
    maxUsersOverride: 'Team limit override',
    overrideHint: 'Leave empty to use the plan limit.',
    notes: 'Internal notes',
    owner: 'Owner account',
    ownerName: 'Owner name',
    ownerEmail: 'Owner email',
    ownerPassword: 'Owner initial password',
    created: 'Merchant created. Store code: {{code}}',
    suspend: 'Suspend',
    activate: 'Activate',
    disable: 'Disable',
    archive: 'Archive',
    archiveConfirm: 'Archive {{name}}? The store code stops working and all sessions end.',
    statusReason: 'Reason (optional)',
    statusConfirm: 'Change status of {{name}} to {{status}}?',
    resetCode: 'Reset store code',
    resetCodeConfirm:
      'Reset the store code for {{name}}? Customers who have not opened the app yet will need the new code. Signed-in customers are not affected.',
    newCode: 'New store code: {{code}}',
    customCode: 'Custom code (optional)',
    featureOverrides: 'Feature overrides',
    panelSecurity: 'Panel sign-in',
    panelSecurityHint: 'People who can sign in to this store’s panel.',
    requireTwoFactor: 'Require two-step verification',
    requireTwoFactorHint: 'Everyone without it must set it up at their next sign-in.',
    isTest: 'Test store',
    isTestHint:
      'For demos and testing. Its subscribers are left out of the platform overview totals.',
    featureOverridesHint:
      'Override the plan for this merchant. "Plan default" removes the override.',
    planDefault: 'Plan default',
    forceOn: 'Force on',
    forceOff: 'Force off',
    details: 'Details',
  },
  plans: {
    title: 'Plans',
    add: 'New plan',
    createTitle: 'New plan',
    editTitle: 'Edit plan',
    code: 'Code',
    codeHint: 'Lower case letters, digits, - and _.',
    maxDevices: 'Max devices',
    maxMerchantUsers: 'Max team members',
    maxHosts: 'Max hosts',
    apiRateLimitPerMinute: 'API requests per minute',
    fullBranding: 'Full branding',
    analytics: 'Analytics',
    gatewayMode: 'Protected gateway',
    active: 'Active',
    limits: 'Limits',
    capabilities: 'Capabilities',
    features: 'Included features',
  },
  releases: {
    title: 'App releases',
    subtitle: 'Publish one version to every device at once. Apps offer the update on their own.',
    add: 'Publish release',
    editTitle: 'Edit release',
    group: { android: 'Android', apple: 'Apple' },
    notPublished: 'Not published yet',
    publishHere: 'Publish',
    requiredBadge: 'Required update',
    minimumBadge: 'Minimum {{version}}',
    devices: 'Devices',
    none: 'None yet',
    version: 'Version',
    currentlyOn: 'Chosen devices are on {{version}}',
    versionDown: 'Previous version number',
    versionUp: 'Next version number',
    belowCurrent: '{{platform}} is already on {{version}}.',
    required: 'Required update',
    requiredHint: 'Older versions must update before they can watch.',
    publishTo: 'Publish to selected ({{count}})',
    publishedTo: 'Published. Devices updated: {{count}}',
    sourceLatest: 'Latest build on GitHub:',
    sourceUse: 'Use it',
    sourceApplied: 'Filled in',
    sourceUnavailable: 'GitHub could not be reached. Enter the link yourself.',
    latestVersion: 'Latest version',
    minimumVersion: 'Minimum version',
    blockedVersions: 'Blocked versions',
    blockedVersionsHint: 'Comma separated, for example 1.0.3, 1.0.4',
    releaseNotes: 'Release notes',
    downloadUrl: 'APK download URL',
    storeUrl: 'Store URL',
    sha256: 'SHA-256 checksum',
    fileSize: 'File size (bytes)',
  },
  featured: {
    title: 'Featured sections',
    subtitle:
      'Round logos at the top of the app home screen. Each one opens the provider categories you pick, like a Netflix or Kids section.',
    add: 'Add section',
    empty:
      'No featured sections yet. Add one to give subscribers a shortcut to what they watch most.',
    emptyTitle: 'Highlight your best content',
    newTitle: 'New featured section',
    editTitle: 'Edit {{name}}',
    name: 'Name',
    namePlaceholder: 'Kids',
    logo: 'Logo',
    logoHint: 'Square PNG, JPG or WebP up to 2 MB. Shown in a circle.',
    logoWrongType: 'Choose a PNG, JPG or WebP image.',
    logoTooLarge: 'The logo must be 2 MB or smaller.',
    shown: 'Shown in the app',
    hidden: 'Hidden',
    categories: 'Categories',
    categoryCount: 'Categories: {{count}}',
    noCategories: 'Pick at least one category below.',
    pickTitle: 'Pick categories',
    pickIntro:
      'Enter a subscriber login of this store to read the provider categories. It is used once and never saved.',
    username: 'Subscriber username',
    password: 'Subscriber password',
    load: 'Show categories',
    reload: 'Load again',
    filter: 'Filter categories',
    noMatch: 'No category matches.',
    sectionLive: 'Live',
    sectionMovie: 'Movies',
    sectionSeries: 'Series',
    tooMany: 'A section can hold up to {{max}} categories.',
    INVALID_CREDENTIALS: 'The provider rejected this username or password.',
    ACCOUNT_EXPIRED: 'This subscription has expired. Use an active one.',
    ACCOUNT_DISABLED: 'This account is disabled. Use an active one.',
    moveUp: 'Move {{name}} up',
    moveDown: 'Move {{name}} down',
    drag: 'Drag to reorder',
    reordered: 'Order saved',
    deleteConfirm: 'Delete the section {{name}}?',
    created: 'Section added',
    limit: 'Up to {{max}} sections.',
  },
  pilot: {
    waitingTitle: 'Version {{version}} is waiting for your test',
    waitingIntro:
      'Only the test accounts below are offered it. Install it on your device, check it, then send it to everyone.',
    testedOn: 'Test devices running it: {{count}}',
    notTestedYet: 'Not on a test device yet',
    built: 'Built {{when}}',
    sendAll: 'Send to everyone',
    sendTitle: 'Send {{version}} to everyone',
    sendIntro: 'Every Android phone, Android TV and Fire TV is offered this version.',
    sendUntested: 'No test device runs this version yet.',
    sent: 'Version {{version}} is on its way to every device.',
    skip: 'Skip',
    skipConfirm: 'Skip version {{version}}? Nobody else is offered it.',
    skipped: 'Skipped. Devices stay on the current version.',
    testersTitle: 'Test accounts',
    testersIntro:
      'Devices signed in with these subscriber accounts get each new version before everyone else.',
    store: 'Store',
    username: 'Subscriber username',
    noDevices: 'No device signed in yet',
    testerAdded: 'Test account added.',
    removeConfirm: 'Stop sending new versions early to {{name}}?',
    linkTitle: 'Download link',
    linkIntro:
      'Always downloads the version you sent to everyone. Use it in the Downloader app and in instructions.',
    copyLink: 'Copy link',
  },
  tvRelease: {
    title: 'Samsung & LG TVs',
    intro: 'TVs load a new version the next time the app is opened. No store review needed.',
    runningNow: 'TVs run now',
    noneYet: 'None yet: TVs run the copy inside the installed app',
    waitingTitle: 'Version {{version}} is waiting for your test',
    waitingIntro:
      'Only TVs signed in with the test accounts (the same ones as Android) load it. Open the app on one, check it, then send it to everyone.',
    testedOn: 'Test TVs running it: {{count}}',
    notTestedYet: 'Not on a test TV yet',
    sendConfirm:
      'Send version {{version}} to every Samsung and LG TV? Each one switches to it the next time the app is opened.',
    sendConfirmUntested:
      'No test TV runs version {{version}} yet. Send it to every Samsung and LG TV anyway?',
    sent: 'Version {{version}} reaches every TV the next time the app is opened.',
    skipConfirm: 'Skip version {{version}}? No other TV gets it.',
    skipped: 'Skipped. TVs stay on the current version.',
  },
  globalSettings: {
    title: 'Global settings',
    maintenance: 'Platform maintenance',
    maintenanceHint: 'Every app shows the maintenance message. Panels stay available.',
    features: 'Global feature switches',
    featuresHint: 'A feature turned off here is off for every merchant.',
  },
  hostLibrary: {
    title: 'Host library',
    subtitle:
      'Every host your merchants saved under a provider, gathered by provider name. Send working hosts to any merchant: each gets copies of its own, checked with its own login, and never sees where they came from.',
    empty: 'No hosts yet. Hosts appear here once a merchant saves them under a provider.',
    workingOf: '{{working}} of {{total}} working',
    storeCount: 'In {{count}} store(s)',
    hostCount: '{{count}} host(s)',
    linkedIntro: 'These merchants get this provider’s new working hosts automatically:',
    unlink: 'Stop sending to {{name}}',
    unlinkAction: 'Stop sending',
    unlinkConfirm:
      'Stop sending new {{provider}} hosts to {{name}}? The hosts it already has stay.',
    unlinked: 'New hosts will no longer be sent to this merchant',
    pickWorking: 'Tick the working ones',
    pickNone: 'Clear',
    sendAll: 'Send to a merchant',
    sendPicked: 'Send {{count}} ticked',
    sendTitle: 'Send {{name}} hosts',
    sendAllIntro:
      'The merchant gets the {{count}} working {{name}} hosts it does not have yet, and stays linked: hosts that start working later reach it automatically.',
    sendPickedIntro: 'The merchant gets the {{count}} ticked host(s) it does not have yet, once.',
    noneWorking: 'No host of this provider works right now. Tick the hosts to send instead.',
    store: 'Merchant',
    pickStore: 'Choose a merchant',
    send: 'Send',
    resultAdded: '{{count}} added',
    resultExisting: '{{count}} already there',
    resultOverLimit: '{{count}} left out: the merchant’s plan has no room',
    upload: 'Link for adding hosts',
    uploadTitle: 'Add new hosts, tested from Saudi Arabia',
    uploadIntro:
      'A private link for you: open it on your phone in Saudi Arabia, enter the provider, a login and the new hosts, and each host is tested from your network before any subscriber gets it. Then add the working ones with one tap.',
    uploadStoreIntro:
      'The hosts are added to this store under their provider, where our server keeps checking them, and from there they reach every merchant linked to the provider.',
    uploadStore: 'Store the hosts are added to',
    uploadCreate: 'Create link',
    uploadFor: 'Adds to {{name}} · created {{when}}',
    uploadSecret:
      'Whoever has this link can add hosts. Keep it to yourself, and replace it if it reaches anyone else.',
    uploadRenewConfirm: 'Make a new link? The old one stops working at once.',
    uploadDelete: 'Delete link',
    uploadDeleteConfirm:
      'Delete the link for adding hosts? It stops at once; hosts already added stay.',
    uploadUnavailable: 'Check links are not set up on this platform yet.',
  },
  announcements: {
    title: 'Announcements',
    add: 'New announcement',
    titleField: 'Title',
    body: 'Message',
    severity: 'Severity',
    info: 'Info',
    warning: 'Warning',
    critical: 'Critical',
    audience: 'Audience',
    CUSTOMERS: 'App customers',
    MERCHANTS: 'Merchants',
    target: 'Merchant',
    allMerchants: 'All merchants',
    startsAt: 'Starts',
    endsAt: 'Ends',
    deleteConfirm: 'Delete this announcement?',
  },
  overview: {
    title: 'Platform overview',
    tenants: 'Merchants',
    tenantsHint: 'Active: {{active}} · Suspended: {{suspended}}',
    testStores: 'Test stores are left out of these numbers: {{stores}}.',
    merchant: 'Merchant',
    test: 'Test',
    noMerchants: 'No merchants yet.',
    cellsHint: 'Each number reads: subscribers · devices.',
    hostsDown: 'Hosts down',
    dependencies: 'Dependencies',
    database: 'Database',
    redis: 'Redis',
    ok: 'OK',
    failing: 'Failing',
  },
  growth: {
    newToday: 'New subscribers today',
    newMonth: 'New subscribers, 30 days',
    newHint: 'New devices: {{devices}}',
    downloadsToday: 'Downloads today',
    downloadsMonth: 'Downloads, 30 days',
    updatesHint: 'In-app updates: {{updates}}',
    definition:
      'A new subscriber is a provider account signing in to a store for the first time. A new device is a subscriber signing in on another TV or phone for the first time. Downloads are APK downloads from a browser or the Downloader app, each device once a day; Google Play and iPhone installs are not included.',
    downloadsSince: 'Downloads are counted from {{day}}.',
    downloadsNone: 'No download has been counted yet.',
    dailyTitle: 'New subscribers per day',
    dailyTable: 'New subscribers, devices and downloads per day',
    newSubscribers: 'New subscribers',
    newDevices: 'New devices',
    downloads: 'Downloads',
    updates: 'In-app updates',
    rankingTitle: 'Merchants by new subscribers',
    rankingDescription:
      'Subscribers who signed in with each store code for the first time, most in the last 30 days first.',
    today: 'Today',
    allTime: 'All time',
    share: 'Share of 30 days',
  },
  usage: {
    sections: 'Report sections',
    now: 'Now',
    usage: 'Usage',
    technical: 'Operations',
    growth: 'Growth',
    merchants: 'Merchants',
    system: 'System',
    settings: 'Settings',
    features: 'Features',
    security: 'Security',
    online: 'Online now',
    today: 'Active today',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    audienceHint: 'Subscribers · devices: {{devices}}',
    subscribers: 'Subscribers',
    devices: 'Devices',
    definition:
      'A subscriber is one provider account. A device is one TV or phone of that subscriber: reinstalling the app on it still counts once. Online means the app was open in the last 5 minutes.',
    dailyTitle: 'Subscribers per day',
    dailyDescription: 'Last 30 days, by {{timeZone}} calendar days.',
    dailyTable: 'Subscribers and devices per day',
    day: 'Day',
    platformsTitle: 'Devices by type',
    platformsDescription: 'Devices used in the last 30 days.',
    versionsTitle: 'App versions',
    versionsDescription: 'The version each device used last, over the last 30 days.',
    version: 'Version',
    noActivity: 'Nobody used the app in the last 30 days.',
    devicesInUse: 'Devices signed in',
    devicesInUseHint: '{{used}} of {{limit}} allowed by the plan',
    blocked: 'Blocked devices',
    healthyHosts: 'Healthy hosts',
    hostsDown: '{{count}} down',
    hostsUnchecked: '{{count}} unchecked',
    hostsDisabled: '{{count}} off',
    logins: 'Successful sign-ins (24h)',
    loginFailures: 'Failed sign-ins (24h)',
    loginFailuresHint: 'Failed: {{failed}}',
  },
  errors: {
    VALIDATION_FAILED: 'Some fields are invalid.',
    UNAUTHENTICATED: 'Please sign in.',
    FORBIDDEN: 'You do not have permission to do this.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'This already exists or was changed by someone else.',
    RATE_LIMITED: 'Too many attempts. Please wait and try again.',
    INVALID_CREDENTIALS: 'Email or password is incorrect.',
    INVALID_CODE: 'The code is not correct. Check the time on your phone and try again.',
    ACCOUNT_LOCKED: 'Too many failed attempts. The account is temporarily locked.',
    ACCOUNT_EXPIRED: 'The account has expired.',
    ACCOUNT_DISABLED: 'This account is disabled.',
    DEVICE_LIMIT_REACHED: 'Device limit reached.',
    DEVICE_BLOCKED: 'This device is blocked.',
    SESSION_REVOKED: 'Your session was ended.',
    TENANT_SUSPENDED: 'This merchant account is suspended.',
    TENANT_EXPIRED: 'This merchant subscription has expired.',
    MAINTENANCE: 'The service is under maintenance.',
    PROVIDER_UNAVAILABLE: 'The IPTV host did not respond.',
    PROVIDER_NOT_CONFIGURED: 'No IPTV host is configured.',
    UNSUPPORTED_CONTENT: 'This content is not supported.',
    UPDATE_REQUIRED: 'An app update is required.',
    PLAN_LIMIT_REACHED: 'Your plan limit has been reached.',
    FEATURE_DISABLED: 'This feature is not available on your plan.',
    INTERNAL: 'Something went wrong. Please try again.',
    NETWORK: 'Cannot reach the server. Check your connection.',
  } satisfies Record<ErrorCode | 'NETWORK', string>,
  validation: {
    required: 'This field is required.',
    invalid: 'Invalid value.',
    email: 'Enter a valid email address.',
    url: 'Enter a full http:// or https:// address.',
    color: 'Use a color like #1A2B3C.',
    semver: 'Use a version like 1.4.0.',
    tooShort: 'Too short.',
    tooLong: 'Too long.',
    number: 'Enter a number.',
    range: 'Enter a whole number from {{min}} to {{max}}.',
  },
};

type DeepStringShape<T> = { [K in keyof T]: T[K] extends string ? string : DeepStringShape<T[K]> };
export type PanelCatalog = DeepStringShape<typeof en>;
